import {
  HeadObjectCommand,
  GetObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
  DeleteObjectsCommand,
  ListObjectsV2Command,
  S3Client
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { getS3Client } from '../utilities/CommonUtilities';
import { AvatarSizeType } from '../components/das-finsuite/baseball-cards/utils/avatarCaptureUtils';

export enum AvatarGenerationStatus {
  PENDING = 'pending',
  GENERATING = 'generating',
  SUCCESS = 'success',
  FAILED = 'failed'
}

export interface AvatarExistsResult {
  exists: boolean;
  url?: string;
}

export interface AvatarUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface BulkGenerationProgress {
  total: number;
  completed: number;
  failed: number;
  currentEmployee?: string;
  status: 'in-progress' | 'completed' | 'failed';
}

export class BaseballCardStorageService {
  private bucketName: string;
  private s3Client: S3Client | undefined;

  constructor(stage: string = 'alpha') {
    const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
    this.bucketName = `baseball-cards-${normalizedStage}`;
    this.s3Client = getS3Client();
  }

  private generateAvatarKey(login: string, size: AvatarSizeType): string {
    return `avatars/${login}/${size}.png`;
  }

  private generateLeaderPrefix(leaderLogin: string): string {
    return `leaders/${leaderLogin}/`;
  }

  async checkAvatarExists(login: string, size: AvatarSizeType): Promise<AvatarExistsResult> {
    const key = this.generateAvatarKey(login, size);
    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    try {
      const command = new HeadObjectCommand({
        Bucket: this.bucketName,
        Key: key
      });

      await client.send(command);
      const url = await this.getAvatarUrl(login, size);
      return { exists: true, url };
    } catch (error: any) {
      if (error.name === 'NotFound' || error.$metadata?.httpStatusCode === 404) {
        return { exists: false };
      }
      throw error;
    }
  }

  async getAvatarImage(login: string, size: AvatarSizeType): Promise<ArrayBuffer | null> {
    const key = this.generateAvatarKey(login, size);
    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    try {
      const command = new GetObjectCommand({
        Bucket: this.bucketName,
        Key: key
      });

      const response = await client.send(command);

      if (response.Body) {
        const bodyString = await response.Body.transformToString('base64');
        const binaryString = atob(bodyString);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      }

      return null;
    } catch (error: any) {
      if (error.name === 'NoSuchKey' || error.$metadata?.httpStatusCode === 404) {
        return null;
      }
      throw error;
    }
  }

  async getAvatarUrl(login: string, size: AvatarSizeType): Promise<string> {
    const key = this.generateAvatarKey(login, size);
    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    const command = new GetObjectCommand({
      Bucket: this.bucketName,
      Key: key
    });

    return await getSignedUrl(client, command, { expiresIn: 3600 });
  }

  async uploadAvatar(login: string, size: AvatarSizeType, imageBuffer: ArrayBuffer): Promise<AvatarUploadResult> {
    const key = this.generateAvatarKey(login, size);
    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    try {
      const command = new PutObjectCommand({
        Bucket: this.bucketName,
        Key: key,
        Body: new Uint8Array(imageBuffer),
        ContentType: 'image/png',
        CacheControl: 'max-age=86400'
      });

      await client.send(command);
      const url = await this.getAvatarUrl(login, size);
      return { success: true, url };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to upload avatar'
      };
    }
  }

  async deleteAvatar(login: string, size: AvatarSizeType): Promise<boolean> {
    const key = this.generateAvatarKey(login, size);
    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    try {
      const command = new DeleteObjectCommand({
        Bucket: this.bucketName,
        Key: key
      });

      await client.send(command);
      return true;
    } catch {
      return false;
    }
  }

  async listLeaderAvatars(leaderLogin: string): Promise<string[]> {
    const prefix = this.generateLeaderPrefix(leaderLogin);
    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucketName,
        Prefix: prefix
      });

      const response = await client.send(command);
      return response.Contents?.map((obj) => obj.Key || '').filter((key) => key !== '') || [];
    } catch {
      return [];
    }
  }

  async deleteLeaderAvatars(leaderLogin: string): Promise<number> {
    const keys = await this.listLeaderAvatars(leaderLogin);

    if (keys.length === 0) {
      return 0;
    }

    const client = this.s3Client;

    if (!client) {
      throw new Error('S3 client not initialized');
    }

    try {
      const command = new DeleteObjectsCommand({
        Bucket: this.bucketName,
        Delete: {
          Objects: keys.map((key) => ({ Key: key }))
        }
      });

      await client.send(command);
      return keys.length;
    } catch {
      return 0;
    }
  }

  getBucketName(): string {
    return this.bucketName;
  }
}
